import React from "react";
import {
  AlertIndicator,
  alertTypes,
  ModalButtonsContainer,
  TextButton,
} from "@SeemplicityDev/shared";
import LocationsTree from "../../../../AppView/ScopeManagement/LocationsTree/LocationsTree";
import type { TreeNode } from "../../../../Components/Tree/types";
import type { ScopeNodeType } from "./types";
import "./EditScopeLocationModal.scss";

export interface EditScopeLocationModalProps {
  nodeName?: string;
  nodeType: ScopeNodeType;
  root?: TreeNode[] | null;
  location?: TreeNode | null;
  onLocationChange: (node: TreeNode) => void;
  // The node is its parent group's only child, so moving it leaves that group empty.
  leavesParentEmpty?: boolean;
  // Set when the node's name is already taken at the chosen location; disables Change.
  collisionError?: string;
  changeDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const WARNING_MESSAGE =
  "Changing scopes' location alters the scopes hierarchy and may change which resources are targeted by connected rules and automations.";

const EMPTY_PARENT_MESSAGE: Record<ScopeNodeType, string> = {
  scope:
    "Moving this scope will leave the parent group empty, which may cause issue for the connected rules, automations and dashboard widgets.",
  group:
    "Moving this scope group will leave the parent group empty, which may cause issue for the connected rules, automations and dashboard widgets.",
};

const NODE_TYPE_NOUN: Record<ScopeNodeType, string> = {
  scope: "scope",
  group: "scope group",
};

const EditScopeLocationModal: React.FC<EditScopeLocationModalProps> = ({
  nodeName,
  nodeType,
  root,
  location,
  onLocationChange,
  leavesParentEmpty = false,
  collisionError,
  changeDisabled = false,
  onConfirm,
  onCancel,
}) => (
  <div className={"modal-text edit-scope-location-modal"}>
    <div>
      <div>
        {"Select the parent group for "}
        <span className={"modal-live-text"}>{nodeName}</span>
        {"."}
      </div>
      <div>
        {
          "All rules and automations applicable to parent hierarchy will be applied to "
        }
        <span className={"modal-live-text"}>{nodeName}</span>
        {` ${NODE_TYPE_NOUN[nodeType]} as well.`}
      </div>
    </div>

    <div
      className={"edit-scope-location-warning"}
      data-testid={"edit-scope-location-warning"}
    >
      <AlertIndicator type={alertTypes.WARNING} content={WARNING_MESSAGE} />
    </div>

    <div>
      <LocationsTree
        name={"location"}
        label={null}
        noMargin
        value={location}
        onChange={onLocationChange}
        // The field renders the collision like any input error, right under the input.
        error={collisionError ? { message: collisionError } : null}
        root={root}
        popoverClassName={"tree-in-scope-location-modal"}
      />
    </div>

    {leavesParentEmpty && (
      <div
        className={`edit-scope-location-empty-parent ${collisionError ? "has-error" : ""}`}
        data-testid={"edit-scope-location-empty-parent"}
      >
        {EMPTY_PARENT_MESSAGE[nodeType]}
      </div>
    )}

    <ModalButtonsContainer>
      <TextButton
        buttonStyle={"secondary"}
        label={"Cancel"}
        onClick={onCancel}
        data-testid={"cancel-edit-scope-location"}
      />
      <TextButton
        buttonStyle={"primary"}
        label={"Change"}
        onClick={onConfirm}
        disabled={changeDisabled || !!collisionError}
        data-testid={"confirm-edit-scope-location"}
      />
    </ModalButtonsContainer>
  </div>
);

export default EditScopeLocationModal;
